import { drawDOM, exportImage } from '@progress/kendo-drawing'
import { createRoot } from 'react-dom/client'

import { exists, waitImmediate } from '../generals'

const svgVarAttributes = ['fill', 'stroke', 'color', 'stop-color'] as const

// 画像保存時の背景が黒のためテキストの色を反転
const textColorSwap = new Map([
  ['--colorFontNormal', '--colorFontLight'],
  ['--colorFontLight', '--colorFontNormal'],
])

const resolveCssVarsInElement = (root: HTMLElement): (() => void) => {
  const computedRoot = getComputedStyle(document.documentElement)
  const cache = new Map<string, string>()

  const resolveVar = (
    value: string,
    swapMap?: ReadonlyMap<string, string>,
  ): string =>
    value.replace(
      /var\((--[\w-]+)(?:,\s*([^)]+))?\)/g,
      (_, name: string, fallback: string | undefined) => {
        const lookupName = swapMap?.get(name) ?? name
        if (!cache.has(lookupName)) {
          cache.set(
            lookupName,
            computedRoot.getPropertyValue(lookupName).trim() || fallback || '',
          )
        }

        return cache.get(lookupName) ?? ''
      },
    )

  const restoreFunctions: (() => void)[] = []

  const traverse = (element: Element) => {
    if (element instanceof SVGElement) {
      const swapMap =
        element instanceof SVGTextElement ? textColorSwap : undefined

      for (const attribute of svgVarAttributes) {
        const value = element.getAttribute(attribute)
        if (value?.includes('var(')) {
          element.setAttribute(attribute, resolveVar(value, swapMap))
          restoreFunctions.push(() => {
            element.setAttribute(attribute, value)
          })
        }
      }
      const styleValue = element.getAttribute('style')
      if (styleValue?.includes('var(')) {
        element.setAttribute('style', resolveVar(styleValue, swapMap))
        restoreFunctions.push(() => {
          element.setAttribute('style', styleValue)
        })
      }
    }
    for (const child of [...element.children]) {
      traverse(child)
    }
  }

  traverse(root)

  return () => {
    restoreFunctions.forEach((restore) => {
      restore()
    })
  }
}

export const createImageData = async (
  image: HTMLElement | React.ReactElement,
  name = 'image',
): Promise<[string | null, string]> => {
  if (image instanceof HTMLElement) {
    const clone = image.cloneNode(true) as HTMLElement
    clone.style.position = 'absolute'
    clone.style.top = '0'
    clone.style.left = '-10000px'
    document.body.appendChild(clone)
    const restore = resolveCssVarsInElement(clone)
    try {
      const group = await drawDOM(clone)
      const data = exists(group) ? await exportImage(group) : null

      return [data, `${name}.png`]
    } finally {
      restore()
      document.body.removeChild(clone)
    }
  }

  const div = document.createElement('div')
  div.style.position = 'absolute'
  div.style.top = '0'
  div.style.left = '-10000px'
  const root = createRoot(div)
  root.render(image)

  await waitImmediate()

  document.body.appendChild(div)
  const restore = resolveCssVarsInElement(div)
  try {
    const group = await drawDOM(div)
    const data = exists(group) ? await exportImage(group) : null

    return [data, `${name}.png`]
  } finally {
    restore()
    document.body.removeChild(div)
  }
}

/**
 * ヒートマップの比率を計算します。
 *
 * @param value - 値。
 * @param max - 最大値。
 * @param min - 最小値。
 * @returns ヒートマップの比率。
 */
export const calcHeatmapRatio = (value: number, max: number, min: number) =>
  max === 0 || min === 0 ? 0 : 0 < value ? value / max : (value / min) * -1

/**
 * 存在する場合に値をセットします。
 *
 * @template T - 値の型。
 * @param value - 値。
 * @param set - セットする関数。
 */
export const setValueIfExist = <T>(
  value: T | undefined,
  set: (value: T) => void,
) => {
  if (exists(value)) {
    set(value)
  }
}
